/**
 * knownPlaces — the character-scoped list of maps a character has personally
 * entered: pocket maps (HNR-B11 / HNR-G10) and, since 2026-10-30, every zone,
 * which the World Map's fog of war reads (worldMapFog.ts).
 *
 * Discovery is presentation state only: the maps and their passages stay in
 * the authoritative portal graph and pocket registry whether or not a
 * character knows them. Only real pocket maps (pocketMaps.ts) and zones with
 * authored content (zones.gen.ts) are accepted, so a stale save or malformed
 * message can never smuggle an arbitrary id in.
 */
import { isPocketMap } from './pocketMaps';
import { ZONE_CONFIGS } from '../content/generated/zones.gen';

/** Bound on the persisted list; the pocket and zone registries are far smaller than this. */
export const MAX_KNOWN_PLACES = 128;

function isKnowablePlace(id: unknown): id is string {
  return typeof id === 'string' && (isPocketMap(id) || Object.prototype.hasOwnProperty.call(ZONE_CONFIGS, id));
}

/** Keep only distinct, real pocket-map and zone ids, in first-discovered order. */
export function normalizeKnownPlaces(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const known: string[] = [];
  for (const id of value) {
    if (!isKnowablePlace(id) || known.includes(id)) continue;
    known.push(id);
    if (known.length >= MAX_KNOWN_PLACES) break;
  }
  return known;
}

/** The list with `mapId` discovered, or null when it adds nothing (not a knowable place, or already known). */
export function withDiscoveredPlace(known: readonly string[], mapId: string | null | undefined): string[] | null {
  if (!isKnowablePlace(mapId) || known.includes(mapId)) return null;
  return normalizeKnownPlaces([...known, mapId]);
}
